import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type {
  PlantSchemaResponse,
  DeviceDto,
  NodeDto,
  LeafDto,
} from '../api/types';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import './LeafPicker.css';

interface Props {
  plantId: string;
  /** Currently selected leaf id, if any. Used to highlight the picked row
   *  and to seed the search box with its symbol as a starting point. */
  currentLeafId?: string | null;
  onSelect(leaf: LeafDto): void;
  onClose(): void;
}

/**
 * Modal that lets the operator pick one leaf out of a plant's schema tree.
 * Reuses the plant's <c>GetSchemaResponse</c> from the API and renders the
 * Device → Node → Leaf hierarchy as a searchable, keyboard-friendly list.
 * The picker deliberately keeps state small: no drag, no multi-select, no
 * expand-all toggle — a widget binds to exactly one leaf.
 */
export function LeafPicker({ plantId, currentLeafId, onSelect, onClose }: Props) {
  const state = useAsync(
    (signal) => api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal),
    [plantId],
  );

  const [filter, setFilter] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Flatten to a single list of {path, leaf} pairs. Keeps rendering + filter
  // logic simple: no recursive tree walk in JSX, no expand/collapse state to
  // remember across re-renders. The path is the human-readable breadcrumb
  // the operator sees (e.g. "verdichter.1 › kessel.1 › t_flamme").
  const rows = useMemo(() => {
    if (state.status !== 'ok') return [] as PickerRow[];
    return flatten(state.data);
  }, [state]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.leaf.symbol.toLowerCase().includes(q)
        || (r.leaf.displayName ?? '').toLowerCase().includes(q)
        || r.path.toLowerCase().includes(q)
        || (r.leaf.unit ?? '').toLowerCase().includes(q),
    );
  }, [rows, filter]);

  const onInputKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && filtered.length === 1) {
      e.preventDefault();
      onSelect(filtered[0].leaf);
    }
  };

  return (
    <div
      class="hv-leaf-picker"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hv-leaf-picker-title"
    >
      <div class="hv-leaf-picker__backdrop" onClick={onClose} />
      <div class="hv-leaf-picker__panel">
        <header class="hv-leaf-picker__header">
          <div>
            <h2 id="hv-leaf-picker-title" class="hv-leaf-picker__title">
              Datenpunkt wählen
            </h2>
            <p class="hv-leaf-picker__subtitle muted">
              {state.status === 'ok' ? state.data.name : '…'}
            </p>
          </div>
          <button
            type="button"
            class="hv-leaf-picker__close"
            onClick={onClose}
            aria-label="Schließen"
            title="Schließen"
          >
            ×
          </button>
        </header>

        <div class="hv-leaf-picker__filter-row">
          <input
            ref={inputRef}
            type="search"
            class="hv-leaf-picker__filter"
            placeholder="Symbol, Name, Pfad oder Einheit…"
            value={filter}
            onInput={(e) => setFilter((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={onInputKey}
            aria-label="Datenpunkte filtern"
          />
          <span class="hv-leaf-picker__count muted">
            {filter
              ? `${filtered.length} / ${rows.length}`
              : `${rows.length} Datenpunkte`}
          </span>
        </div>

        <div class="hv-leaf-picker__body">
          {state.status === 'loading' && <p class="muted">Schema wird geladen…</p>}
          {state.status === 'error' && (
            <p class="hv-leaf-picker__error">Anlagen-Schema konnte nicht geladen werden.</p>
          )}
          {state.status === 'ok' && rows.length === 0 && (
            <p class="muted">Diese Anlage enthält keine Datenpunkte.</p>
          )}
          {state.status === 'ok' && rows.length > 0 && filtered.length === 0 && (
            <p class="muted">Keine Treffer für „{filter}".</p>
          )}
          {filtered.length > 0 && (
            <ul class="hv-leaf-picker__list" role="listbox">
              {filtered.map((r) => (
                <li key={r.leaf.id}>
                  <button
                    type="button"
                    class={`hv-leaf-picker__row${r.leaf.id === currentLeafId ? ' hv-leaf-picker__row--selected' : ''}`}
                    onClick={() => onSelect(r.leaf)}
                    role="option"
                    aria-selected={r.leaf.id === currentLeafId}
                  >
                    <span class="hv-leaf-picker__row-main">
                      <span class="hv-leaf-picker__row-name">
                        {r.leaf.displayName ?? r.leaf.symbol}
                      </span>
                      <span class="hv-leaf-picker__row-symbol mono muted">
                        {r.leaf.displayName ? `(${r.leaf.symbol})` : ''}
                      </span>
                    </span>
                    <span class="hv-leaf-picker__row-path muted">{r.path}</span>
                    <span class="hv-leaf-picker__row-meta">
                      <span class="hv-leaf-picker__row-type">{r.leaf.valueType}</span>
                      {r.leaf.unit && (
                        <span class="hv-leaf-picker__row-unit muted">{r.leaf.unit}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PickerRow {
  path: string;
  leaf: LeafDto;
}

/**
 * Walks the plant schema and produces one row per leaf, carrying the full
 * device/node breadcrumb as a display-friendly path. Recursion is bounded by
 * the tree depth (typically ≤ 6 levels), so a stack walk is fine.
 */
function flatten(plant: PlantSchemaResponse): PickerRow[] {
  const rows: PickerRow[] = [];
  for (const device of plant.devices) {
    walkDevice(device, rows);
  }
  return rows;
}

function walkDevice(device: DeviceDto, rows: PickerRow[]): void {
  for (const node of device.nodes) {
    walkNode(node, [device.symbol], rows);
  }
}

function walkNode(node: NodeDto, prefix: string[], rows: PickerRow[]): void {
  const here = [...prefix, node.symbol];
  const pathStr = here.join(' › ');
  for (const leaf of node.leaves) {
    rows.push({ path: pathStr, leaf });
  }
  for (const child of node.children) {
    walkNode(child, here, rows);
  }
}
